<template>
  <CommonDialog v-model:is-active="isActive">
    <template #header> <h1>Bildauschnitt</h1></template>
    <template #content
      ><v-container class="">
        <v-row>
          <v-col>
            <h2>Neuer Ausschnitt:</h2>
          </v-col>
        </v-row>
        <v-row class="img-container">
          <v-col>
            <vue-cropper
              v-if="props.originalDownloadUrl !== null"
              ref="cropper"
              :guides="true"
              :responsive="true"
              :view-mode="2"
              :background="false"
              :rotatable="true"
              :src="props.originalDownloadUrl"
              drag-mode="move"
              alt="Source Image"
            />
            <v-alert v-else type="warning">Kein Bild hochgeladen.</v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn-group color="primary" variant="outlined" divided>
              <v-btn
                type="button"
                prepend-icon="mdi-magnify-plus-outline"
                @click="zoomIn"
              >
                Zoom In
              </v-btn>

              <v-btn
                prepend-icon="mdi-magnify-minus-outline"
                type="button"
                @click="zoomOut"
              >
                Zoom Out
              </v-btn>

              <v-btn
                type="button"
                prepend-icon="mdi-rotate-right"
                @click="rotate"
              >
                Rotieren
              </v-btn>
            </v-btn-group>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn-group>
              <v-btn
                type="button"
                prepend-icon="mdi-crop"
                color="success"
                @click="askCropImage"
              >
                Speichern
              </v-btn>
              <v-btn
                type="button"
                color="error"
                variant="outlined"
                @click="cancel"
              >
                Abbrechen
              </v-btn>
            </v-btn-group>
          </v-col>
        </v-row>
      </v-container></template
    >
  </CommonDialog>
</template>

<script setup lang="ts">
import 'cropperjs/dist/cropper.css'

import hotkeys from 'hotkeys-js'
import { onMounted, Ref, ref } from 'vue'
import VueCropper from 'vue-cropperjs'

import { useConfirmationDialog } from '@/util/composables/useConfirmationDialog'

import CommonDialog from '../../CommonDialog.vue'

type CropModalProps = {
  originalDownloadUrl: string
}

const emit = defineEmits(['save', 'cancel'])

const isActive = defineModel<boolean>('isActive')
const props = defineProps<CropModalProps>()
const cropper = ref(null)
const cropImg: Ref<string> = ref('')

onMounted(() => {
  hotkeys('esc', (event) => {
    event.preventDefault()
    cancel()
  })
})

function cancel() {
  emit('cancel')
}

function rotate() {
  cropper.value.rotate(90)
  // Zoom out so that the whole image is in view after rotating
  cropper.value.zoomTo(0)
}

function zoomIn() {
  cropper.value.relativeZoom(0.1)
}

function zoomOut() {
  cropper.value.relativeZoom(-0.1)
}

function dataURLtoBlob(dataurl) {
  let arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], { type: mime })
}

const confirm = useConfirmationDialog()

async function askCropImage() {
  const isConfirmed = await confirm({
    content:
      'Sind Sie sicher, dass Sie den Bildausschnitt überschreiben wollen?',
  })
  if (isConfirmed) cropImage()
}

async function cropImage() {
  // get image data for post processing, e.g. upload or setting image src
  cropImg.value = cropper.value.getCroppedCanvas().toDataURL()
  const blob = dataURLtoBlob(cropImg.value)
  emit('save', blob)
}
</script>

<style lang="scss" scoped>
.img-container {
  margin: 20px auto;
}

.crop-modal {
  width: 850px;
}

.vue-cropper {
  width: 400px;
  height: 500px;
}
</style>
